// Redux
import { AsyncResult, Result } from '../..';
import { LOAD_BENCHMARK_FOR_EDIT, RESET_GIF, RESET_SELECTED_CONTACT, SET_COMPARISON_IS_FOCUSED, TOGGLE_PORTFOLIO_EDITOR } from './actions';
import { getManyFundsData } from '../funds/fund-store';
import { getComparisonBenchmark, getManyComparisonBenchmarkData } from '../comparison-benchmarks/comparison-benchmarks-store';

// Utils
import { COMPARISON_NAV_PATHS } from '../../../utils/constants';
import { PROJECTION_BASIS_TYPES } from '../../../utils/bmo-gif/constants';
import { getCustomReturnRolling } from '../../../utils/bmo-gif/calculate';
import { formatMessage } from '../../../utils/i18n';
import BenchmarkPortfolio from '@/types/benchmark-portfolio';
import ComparisonItem from '@/types/comparison-item';
import ComparisonBenchmark from '@/types/comparison-benchmark';
import AppMode from '@/types/app-mode';
import BMOGIFPortfolio from '@/types/bmo-gif/bmo-gif-portfolio';
import BMOPortfolioData from '@/types/bmo-gif/bmo-portfolio-data';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';

// Comparison
export const setComparisonIsFocused =
  (isFocused: boolean): Result<void> =>
  dispatch => {
    dispatch({ ...SET_COMPARISON_IS_FOCUSED, isFocused });
  };

export const togglePortfolioEditor =
  (isVisible: boolean, isPinned = false, isFocused = false, isReadOnly = false): Result<void> =>
  (dispatch, getState) => {
    const state = getState();
    const { user } = state.auth;

    // We want to deprecate the pinned editor. So we will only allow the pinned editor if the user has the permission.
    // If the user does not have the permission, we will always use the full Portfolio editor.
    // TODO: Once ready, we should cleanup all code related to the pinned editor.

    const allowPinned = userHasPermission(user, PERMISSIONS.PORTFOLIO_EDITOR_PINNED);
    const isPinnedEditorActive = allowPinned ? isPinned : false;

    // The Risk tab has its own embedded editor, so we're not using the modal/pinned version
    if (getState().comparison.tab !== COMPARISON_NAV_PATHS.RISK) {
      dispatch({
        ...TOGGLE_PORTFOLIO_EDITOR,
        isVisible,
        isPinned: isPinnedEditorActive,
        isFocused,
        isReadOnly,
      });
    }
  };

// BMO GIF
export const resetGif = (): Result<void> => dispatch => {
  dispatch(RESET_GIF);
};

export const getBMOPortfolioHoldingsData =
  (portfolio: BMOGIFPortfolio): AsyncResult<BMOPortfolioData> =>
  async dispatch => {
    const ids = portfolio.holdings.map(h => h.id);
    const [funds] = await Promise.all([dispatch(getManyFundsData(ids))]);
    if (portfolio.projectionBasis !== undefined) {
      switch (portfolio.projectionBasis.type) {
        case PROJECTION_BASIS_TYPES.CUSTOM: {
          const returnData = getCustomReturnRolling(portfolio.customRate || 0);
          return {
            funds,
            benchmark: returnData,
            fixedIncome: returnData,
          };
        }
        case PROJECTION_BASIS_TYPES.BENCHMARK: {
          const benchmark = await dispatch(getComparisonBenchmark(portfolio.projectionBasis.refId));
          const fixedIncome = await dispatch(getComparisonBenchmark(benchmark.riskFreeId || ''));
          return {
            funds,
            benchmark: benchmark.performance.rolling.monthly,
            fixedIncome: fixedIncome.performance.rolling.monthly,
          };
        }
        default: {
          return {
            funds,
            benchmark: [],
            fixedIncome: [],
          };
        }
      }
    }
    return {
      funds,
      benchmark: [],
      fixedIncome: [],
    };
  };

// Benchmark
export const loadBenchmarkForEdit =
  (benchmarkPortfolio: BenchmarkPortfolio, item?: ComparisonItem): Result<void> =>
  (dispatch, getState) => {
    const {
      intl: { locale },
    } = getState();

    const copy = { ...benchmarkPortfolio };

    if (!benchmarkPortfolio.dates.savedAt && item) {
      if (benchmarkPortfolio.holdings.length === 1) {
        copy.name = formatMessage('HoldingsEditor.NewName.BENCHMARK');
      } else {
        copy.name = item.name[locale] || formatMessage('HoldingsEditor.NewName.BENCHMARK');
      }
    }

    dispatch(setComparisonIsFocused(true));
    dispatch({ ...LOAD_BENCHMARK_FOR_EDIT, benchmark: copy });
  };

export const getBenchmarksData =
  (benchmark: BenchmarkPortfolio): AsyncResult<ComparisonBenchmark[]> =>
  async dispatch => {
    const { holdings } = benchmark;
    const data = await dispatch(getManyComparisonBenchmarkData(holdings.map(h => h.id)));

    let riskFree: ComparisonBenchmark[] = [];
    if (data.every(h => !!h.riskFreeId)) {
      riskFree = await dispatch(getManyComparisonBenchmarkData(data.map(h => h.riskFreeId || '')));
    }

    return data.concat(riskFree);
  };

// Contacts
export const resetCurrentContact = (): Result<void> => (dispatch, getState) => {
  const {
    auth: { user },
    households: { currentHousehold },
  } = getState();

  if (user != null) {
    if (user.appConfig.mode === AppMode.CONTACT && !currentHousehold) {
      return;
    }
  }

  dispatch(RESET_SELECTED_CONTACT);
};
